import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { IncidentBannerService, IncidentBannerState } from 'src/app/services/incident-banner.service';
import { SuperAdminContextService } from 'src/app/services/super-admin-context.service';
import { IncidentDetailsDialogComponent } from './incident-details-dialog/incident-details-dialog.component';

const SUPER_ADMIN_BAR_HEIGHT_PX = 36;
const BASE_HEADER_HEIGHT_PX = 80;

@Component({
  selector: 'app-incident-banner',
  templateUrl: './incident-banner.component.html',
  styleUrls: ['./incident-banner.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncidentBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('bannerEl') bannerEl?: ElementRef<HTMLElement>;

  state: IncidentBannerState = { enabled: false, message: '', updated_at: null, updated_by_uid: null };

  private sub?: Subscription;
  private resizeObserver?: ResizeObserver;

  constructor(
    private bannerService: IncidentBannerService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    public superAdminContext: SuperAdminContextService,
  ) {}

  ngOnInit(): void {
    this.sub = this.bannerService.banner$.subscribe((s) => {
      this.state = s;
      this.cdr.detectChanges();
      // Re-observe after state changes so the ResizeObserver picks up a
      // newly-mounted banner element (and we immediately recompute offsets).
      requestAnimationFrame(() => this.observe());
    });
    this.sub.add(
      this.superAdminContext.present$.subscribe(() => {
        requestAnimationFrame(() => this.recompute());
      }),
    );
  }

  ngAfterViewInit(): void {
    this.observe();
  }

  openDetails(): void {
    this.dialog.open(IncidentDetailsDialogComponent, {
      width: '640px',
      height: 'auto',
      maxWidth: '95vw',
      panelClass: 'incident-dialog-panel',
      data: { message: this.state.message },
      autoFocus: false,
      restoreFocus: true,
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.resetOffset();
  }

  private observe(): void {
    this.resizeObserver?.disconnect();
    const el = this.bannerEl?.nativeElement;
    if (!el) {
      this.recompute();
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.recompute());
    this.resizeObserver.observe(el);
    this.recompute();
  }

  private recompute(): void {
    const el = this.bannerEl?.nativeElement;
    const bannerHeight = el && this.state.enabled ? el.getBoundingClientRect().height : 0;
    document.body.classList.toggle('has-incident-banner', bannerHeight > 0);
    const superAdminHeight = this.superAdminContext.present ? SUPER_ADMIN_BAR_HEIGHT_PX : 0;
    this.applyOffset(superAdminHeight + bannerHeight);
  }

  private applyOffset(totalTopPx: number): void {
    const root = document.documentElement;
    if (totalTopPx <= 0) {
      this.resetOffset();
      return;
    }
    root.style.setProperty('--super-admin-menu-height', `${totalTopPx}px`);
    root.style.setProperty('--header-height', `${BASE_HEADER_HEIGHT_PX + totalTopPx}px`);
  }

  private resetOffset(): void {
    const root = document.documentElement;
    document.body.classList.remove('has-incident-banner');
    root.style.removeProperty('--super-admin-menu-height');
    root.style.setProperty('--header-height', `${BASE_HEADER_HEIGHT_PX}px`);
  }
}
